import { SupportHelp } from './components/support/Support'
import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { isIntegrationsPath } from './lib/usps-pickups'
import { hasFeature, FEATURES } from './lib/features'
import { useTheme } from './contexts/ThemeContext'
import { useActivity } from './contexts/ActivityContext'
import OnboardingWizard from './components/OnboardingWizard'
import WhatsNewButton from './components/announcements/WhatsNewButton'
import {
  LayoutList,
  Plus,
  TrendingDown,
  Search,
  User,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  TrendingUp,
  Mail,
  Plug,
  Share2,
  ShoppingCart,
  Package,
  Rocket,
  Calculator
} from 'lucide-react'

// Lazy load all page components for code splitting
const Account = lazy(() => import('./pages/Account'))
const Strategies = lazy(() => import('./pages/Strategies'))
const Listings = lazy(() => import('./pages/Listings'))
const Login = lazy(() => import('./pages/Login'))
const Signup = lazy(() => import('./pages/auth/Signup'))
const Verify = lazy(() => import('./pages/auth/Verify'))
const ResetPassword = lazy(() => import('./pages/auth/ResetPassword'))
const Confirm = lazy(() => import('./pages/auth/Confirm'))
const WasntMe = lazy(() => import('./pages/auth/WasntMe'))
const Pay = lazy(() => import('./pages/auth/Pay'))
const PaySuccess = lazy(() => import('./pages/auth/PaySuccess'))
const OAuthConsent = lazy(() => import('./pages/auth/OAuthConsent'))
const QuickList = lazy(() => import('./pages/QuickList'))
const AutoListBulk = lazy(() => import('./pages/AutoList')) // Renamed: bulk upload option
const AdminSettings = lazy(() => import('./pages/AdminSettings'))
const ListingSettings = lazy(() => import('./pages/ListingSettings'))
const InfluencerCentral = lazy(() => import('./pages/InfluencerCentral'))
const EbayCentral = lazy(() => import('./pages/EbayCentral'))
const Shipping = lazy(() => import('./pages/Shipping'))
const Integrations = lazy(() => import('./pages/Integrations'))
const AutomationBuilder = lazy(() => import('./pages/AutomationBuilder'))
const EmailIntegration = lazy(() => import('./pages/EmailIntegration'))
const SelectSocialAccount = lazy(() => import('./pages/SelectSocialAccount'))
const ProductCRM = lazy(() => import('./pages/ProductCRM'))
const SocialPosts = lazy(() => import('./pages/SocialPosts'))
const TikTokStats = lazy(() => import('./pages/TikTokStats'))
const A2AArbitrage = lazy(() => import('./pages/A2AArbitrage'))
const A2ASavedSellers = lazy(() => import('./pages/A2ASavedSellers'))
const A2AJobRuns = lazy(() => import('./pages/A2AJobRuns'))
const A2AProductFinder = lazy(() => import('./pages/A2AProductFinder'))
const A2AMatchedLeads = lazy(() => import('./pages/A2AMatchedLeads'))
const AmazonToEbayScanner = lazy(() => import('./pages/AmazonToEbayScanner'))
const SellerAsins = lazy(() => import('./pages/SellerAsins'))
const AmazonOrders = lazy(() => import('./pages/AmazonOrders'))
const OrderTracking = lazy(() => import('./pages/OrderTracking'))
const LaunchDashboard = lazy(() => import('./pages/LaunchDashboard'))
const PayoutCalculator = lazy(() => import('./pages/PayoutCalculator'))
const NotFound = lazy(() => import('./pages/NotFound'))
const Privacy = lazy(() => import('./pages/Privacy'))
const Terms = lazy(() => import('./pages/Terms'))
const DataDeletion = lazy(() => import('./pages/DataDeletion'))
const AdminActivity = lazy(() => import('./pages/AdminActivity'))
const ActivityLog = lazy(() => import('./pages/ActivityLog'))

export default function App() {
  const { user, isAuthenticated, signOut } = useAuth()
  const { isDark, toggleTheme } = useTheme()
  const { trackEvent } = useActivity()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const isPublic =
      location.pathname === '/login' ||
      location.pathname === '/privacy' ||
      location.pathname === '/terms' ||
      location.pathname === '/data-deletion' ||
      location.pathname === '/oauth/consent' ||
      location.pathname.startsWith('/auth/')
    if (!isAuthenticated && !isPublic) {
      const redirectPath = `${location.pathname}${location.search}${location.hash}`
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`, { replace: true })
    }
  }, [isAuthenticated, location.pathname, location.search, location.hash, navigate])

  useEffect(() => {
    const hasSellerFeatures = isAuthenticated && user?.features && (
      user.features.includes('a2a') ||
      user.features.includes('seller_asins') ||
      user.features.includes('aura')
    )
    if (hasSellerFeatures && !user?.user_metadata?.onboarding_complete) {
      setShowOnboarding(true)
      return
    }

    setShowOnboarding(false)
  }, [isAuthenticated, user])

  // Track page views on route change
  useEffect(() => {
    if (isAuthenticated) {
      trackEvent('page_view', location.pathname + location.hash, {
        referrer: document.referrer || null,
      })
    }
  }, [location.pathname, location.hash, isAuthenticated, trackEvent])

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      const handleDocumentClick = (event) => {
        const nav = document.querySelector('nav')
        if (nav && !nav.contains(event.target)) {
          setMobileMenuOpen(false)
        }
      }

      document.addEventListener('click', handleDocumentClick)
      return () => document.removeEventListener('click', handleDocumentClick)
    }
  }, [mobileMenuOpen])

  const handleLogin = async () => {
    // Login is handled by AuthContext
    // Navigate to Product CRM as default landing page
    const redirectParam = new URLSearchParams(location.search).get('redirect')
    const redirectPath = redirectParam && redirectParam.startsWith('/') ? redirectParam : '/product-crm'
    navigate(redirectPath, { replace: true })
  }

  const handleLogout = async () => {
    await signOut()
    navigate('/login')
  }

  // These auth routes must render AHEAD of the auth gate: they establish a
  // session mid-flow (a recovery link, or verifyOtp on /auth/confirm), so without
  // this the freshly-signed-in user would be bounced into the dashboard before
  // the flow's own page can run. Keyed on the path (deterministic) rather than a
  // racy PASSWORD_RECOVERY / SIGNED_IN event. /auth/confirm (OPS-380) is the
  // branded action-link handler; it verifies the token then routes onward.
  if (
    location.pathname === '/auth/reset-password' ||
    location.pathname === '/auth/confirm' ||
    location.pathname === '/auth/wasnt-me'
  ) {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-theme-primary flex items-center justify-center">
          <div className="text-theme-secondary">Loading...</div>
        </div>
      }>
        {location.pathname === '/auth/confirm' ? <Confirm />
          : location.pathname === '/auth/wasnt-me' ? <WasntMe />
          : <ResetPassword />}
      </Suspense>
    )
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={
        <div className="min-h-screen bg-theme-primary flex items-center justify-center">
          <div className="text-theme-secondary">Loading...</div>
        </div>
      }>
        <Routes>
          <Route path="/auth/signup" element={<Signup />} />
          <Route path="/auth/verify" element={<Verify />} />
          <Route path="/auth/pay" element={<Pay />} />
          <Route path="/auth/pay/success" element={<PaySuccess />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="*" element={<Login onLogin={handleLogin} />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <div className="min-h-screen bg-theme-primary">
      <nav className="bg-theme-surface border-b border-theme relative z-50">
        {/* Full-bleed container - no padding */}
        <div className="w-full">
          {/* Main Navigation Bar - edge to edge */}
          <div className="flex justify-between items-center h-14">

            {/* Logo Section - FAR LEFT edge */}
            <div className="flex items-center pl-2">
              {/* Mobile: Menu Button + Icon Logo */}
              <div className="flex items-center lg:hidden">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-lg text-theme-secondary hover:text-theme-primary hover:bg-theme-hover focus:outline-none focus:ring-2 focus:ring-accent transition-colors mr-2"
                  aria-expanded={mobileMenuOpen}
                  aria-label="Toggle navigation menu"
                >
                  {mobileMenuOpen ? (
                    <X className="h-5 w-5" strokeWidth={1.5} />
                  ) : (
                    <Menu className="h-5 w-5" strokeWidth={1.5} />
                  )}
                </button>
                <img
                  src="/assets/logos/logo-icon.svg"
                  alt="OpSyncPro"
                  className="h-10 w-auto"
                />
              </div>

              {/* Desktop: Full Logo (bigger) - swap based on theme */}
              <img
                src={isDark ? "/assets/logos/logo-navbar.svg" : "/assets/logos/logo-navbar-light.svg"}
                alt="OpSyncPro"
                className="h-12 w-auto hidden lg:block"
              />
            </div>

            <div className="flex items-center lg:hidden pr-2">
              <WhatsNewButton variant="desktop" />
              <SupportHelp />
              <button onClick={toggleTheme} className="support-target text-theme-secondary rounded-lg" aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </div>

            {/* Desktop Navigation - FAR RIGHT edge */}
            <div className="hidden lg:flex items-center space-x-1 pr-2">
              <Link
                to="/asin-lookup"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/asin-lookup' || location.pathname === '/product-crm' || location.pathname === '/posts'
                    ? 'bg-accent text-white'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                }`}
              >
                Influencer Central
              </Link>
              <Link
                to="/ebay-central"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/ebay-central' || location.pathname === '/listings' || location.pathname === '/'
                    ? 'bg-accent text-white'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                }`}
              >
                Marketplace Central
              </Link>
              <Link
                to="/integrations"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isIntegrationsPath(location.pathname)
                    ? 'bg-accent text-white'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                }`}
              >
                Integrations
              </Link>
              {/* A2A */}
              {hasFeature(user, FEATURES.A2A) && (
                <Link
                  to="/a2a"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/a2a'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  A2A
                </Link>
              )}
              {hasFeature(user, FEATURES.A2E_SOURCING) && (
                <Link
                  to="/sourcing/amazon-to-ebay"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/sourcing/amazon-to-ebay'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  A→E Sourcing
                </Link>
              )}
              {hasFeature(user, FEATURES.ORDER_TRACKING) && (
                <Link
                  to="/orders"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/orders')
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  Orders
                </Link>
              )}
              {/* Seller ASINs */}
              {hasFeature(user, FEATURES.SELLER_ASINS) && (
                <Link
                  to="/seller-asins"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/seller-asins'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  Seller ASINs
                </Link>
              )}
              {/* Product Launch */}
              {hasFeature(user, FEATURES.PL) && (
                <Link
                  to="/pl"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/pl')
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  Product Launch
                </Link>
              )}
              {/* Payout Calculator */}
              {hasFeature(user, FEATURES.PAYOUT_CALCULATOR) && (
                <Link
                  to="/tools/payout-calculator"
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === '/tools/payout-calculator'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                >
                  Payout Calculator
                </Link>
              )}
              <Link
                to="/account"
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/account'
                    ? 'bg-accent text-white'
                    : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                }`}
              >
                Account
              </Link>
              {/* What's New */}
              <WhatsNewButton variant="desktop" />
              <SupportHelp />
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-theme-secondary hover:text-theme-primary hover:bg-theme-hover dark:hover:bg-theme-hover transition-colors"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? (
                  <Sun className="h-5 w-5" strokeWidth={1.5} />
                ) : (
                  <Moon className="h-5 w-5" strokeWidth={1.5} />
                )}
              </button>
              <button
                onClick={handleLogout}
                className="ml-2 bg-theme-hover hover:opacity-80 text-theme-secondary hover:text-theme-primary px-3 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Mobile Menu Panel */}
            <div className="lg:hidden absolute top-14 left-0 right-0 bg-theme-surface border-b border-theme shadow-xl z-50">
              <div className="px-4 py-3 space-y-1">

                {/* User Welcome */}
                {user && (
                  <div className="px-3 py-2 text-theme-tertiary text-sm border-b border-theme mb-2">
                    {user.name || user.username || 'User'}
                  </div>
                )}

                {/* Navigation Links */}
                <Link
                  to="/asin-lookup"
                  className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === '/asin-lookup' || location.pathname === '/product-crm' || location.pathname === '/posts'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Search className="mr-3 h-5 w-5" strokeWidth={1.5} />
                  Influencer Central
                </Link>

                <Link
                  to="/ebay-central"
                  className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === '/ebay-central' || location.pathname === '/listings' || location.pathname === '/'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LayoutList className="mr-3 h-5 w-5" strokeWidth={1.5} />
                  Marketplace Central
                </Link>

                <Link
                  to="/integrations"
                  className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    isIntegrationsPath(location.pathname)
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Plug className="mr-3 h-5 w-5" strokeWidth={1.5} />
                  Integrations
                </Link>

                {/* A2A */}
                {hasFeature(user, FEATURES.A2A) && (
                  <Link
                    to="/a2a"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/a2a'
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <TrendingUp className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    A2A
                  </Link>
                )}

                {/* Amazon to eBay Sourcing */}
                {hasFeature(user, FEATURES.A2E_SOURCING) && (
                  <Link
                    to="/sourcing/amazon-to-ebay"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/sourcing/amazon-to-ebay'
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Search className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    A→E Sourcing
                  </Link>
                )}

                {hasFeature(user, FEATURES.ORDER_TRACKING) && (
                  <Link
                    to="/orders"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname.startsWith('/orders')
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Package className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    Orders
                  </Link>
                )}

                {/* Seller ASINs */}
                {hasFeature(user, FEATURES.SELLER_ASINS) && (
                  <Link
                    to="/seller-asins"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/seller-asins'
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ShoppingCart className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    Seller ASINs
                  </Link>
                )}

                {/* Product Launch */}
                {hasFeature(user, FEATURES.PL) && (
                  <Link
                    to="/pl"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname.startsWith('/pl')
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Rocket className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    Product Launch
                  </Link>
                )}

                {/* Payout Calculator */}
                {hasFeature(user, FEATURES.PAYOUT_CALCULATOR) && (
                  <Link
                    to="/tools/payout-calculator"
                    className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                      location.pathname === '/tools/payout-calculator'
                        ? 'bg-accent text-white'
                        : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Calculator className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    Payout Calculator
                  </Link>
                )}

                <Link
                  to="/account"
                  className={`flex items-center px-3 py-3 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === '/account'
                      ? 'bg-accent text-white'
                      : 'text-theme-secondary hover:text-theme-primary hover:bg-theme-hover'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="mr-3 h-5 w-5" strokeWidth={1.5} />
                  Account
                </Link>

                {/* What's New */}
                <WhatsNewButton variant="mobile" />

                {/* Theme Toggle & Logout */}
                <div className="pt-2 mt-2 border-t border-theme space-y-1">
                  <button
                    onClick={toggleTheme}
                    className="flex items-center w-full px-3 py-3 rounded-lg text-base font-medium text-theme-secondary hover:text-theme-primary hover:bg-theme-hover transition-colors"
                  >
                    {isDark ? (
                      <>
                        <Sun className="mr-3 h-5 w-5" strokeWidth={1.5} />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="mr-3 h-5 w-5" strokeWidth={1.5} />
                        Dark Mode
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      handleLogout()
                      setMobileMenuOpen(false)
                    }}
                    className="flex items-center w-full px-3 py-3 rounded-lg text-base font-medium text-theme-secondary hover:text-error hover:bg-error/10 transition-colors"
                  >
                    <LogOut className="mr-3 h-5 w-5" strokeWidth={1.5} />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </nav>

      <main className={['/listings', '/asin-lookup', '/ebay-central', '/product-crm', '/pl'].includes(location.pathname) || location.pathname === '/' || location.pathname.startsWith('/a2a') || location.pathname.startsWith('/orders') || location.pathname.startsWith('/integrations/shipping/') ? 'w-full' : 'max-w-7xl mx-auto py-4 px-2 sm:py-6 sm:px-6 lg:px-8'}>
        <div className={['/listings', '/asin-lookup', '/ebay-central', '/product-crm', '/pl'].includes(location.pathname) || location.pathname === '/' || location.pathname.startsWith('/a2a') || location.pathname.startsWith('/orders') ? '' : 'sm:px-0'}>
          <Suspense fallback={
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                <p className="mt-2 text-theme-secondary">Loading...</p>
              </div>
            </div>
          }>
            <Routes>
              <Route path="/" element={<Navigate to="/asin-lookup#product-crm" replace />} />
              <Route path="/oauth/consent" element={<OAuthConsent />} />
              <Route path="/ebay-central" element={<EbayCentral />} />
              <Route path="/listings" element={<Listings />} />
              <Route path="/auto-list" element={<QuickList />} />
              <Route path="/bulk-list" element={<AutoListBulk />} />
              <Route path="/strategies" element={<Strategies />} />
              <Route path="/account" element={<Account />} />
              <Route path="/listing-settings" element={<ListingSettings />} />
              <Route path="/admin-settings" element={<AdminSettings />} />
              <Route path="/asin-lookup" element={<InfluencerCentral />} />
              <Route path="/integrations" element={<Integrations />} />
              <Route path="/integrations/shipping/pickups" element={<Shipping key="pickups" />} />
              <Route path="/integrations/shipping/addresses" element={<Shipping key="addresses" addressesPage />} />
              {/* Standalone /automations list redirects into Inbox > Automations tab. */}
              <Route path="/automations" element={<Navigate to="/asin-lookup?tab=automations#inbox" replace />} />
              <Route path="/automations/:flowId" element={<AutomationBuilder />} />
              <Route path="/email-integration" element={<EmailIntegration />} />
              <Route path="/integrations/select-account" element={<SelectSocialAccount />} />
              <Route path="/social/tiktok-stats" element={<TikTokStats />} />
              <Route path="/social/compose" element={<Navigate to="/asin-lookup#posts" replace />} />
              <Route path="/api-keys" element={<Navigate to="/integrations" replace />} />
              <Route path="/settings" element={<Navigate to="/integrations" replace />} />
              <Route path="/product-crm" element={<Navigate to="/asin-lookup#product-crm" replace />} />
              <Route path="/posts" element={<Navigate to="/asin-lookup#posts" replace />} />
              <Route path="/crm" element={<Navigate to="/asin-lookup" replace />} />
              {hasFeature(user, FEATURES.A2A) && (
                <>
                  <Route path="/a2a" element={<A2AArbitrage />} />
                  <Route path="/a2a/sellers" element={<A2ASavedSellers />} />
                  <Route path="/a2a/runs" element={<A2AJobRuns />} />
                  <Route path="/a2a/leads" element={<A2AMatchedLeads />} />
                  <Route path="/a2a/finder" element={<A2AProductFinder />} />
                </>
              )}
              {hasFeature(user, FEATURES.A2E_SOURCING) && (
                <Route path="/sourcing/amazon-to-ebay" element={<AmazonToEbayScanner />} />
              )}
              {hasFeature(user, FEATURES.ORDER_TRACKING) && (
                <Route path="/orders" element={<OrderTracking />} />
              )}
              <Route path="/seller-asins" element={<SellerAsins />} />
              <Route path="/amazon-orders" element={<AmazonOrders />} />
              {hasFeature(user, FEATURES.PL) && (
                <Route path="/pl" element={<LaunchDashboard />} />
              )}
              {hasFeature(user, FEATURES.PAYOUT_CALCULATOR) && (
                <Route path="/tools/payout-calculator" element={<PayoutCalculator />} />
              )}
              {/* Redirect /login to default page if already authenticated */}
              <Route path="/admin/activity" element={<AdminActivity />} />
              <Route path="/activity" element={<ActivityLog />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              <Route path="/login" element={<Navigate to="/asin-lookup" replace />} />
              <Route path="/auth/signup" element={<Signup />} />
              <Route path="/auth/verify" element={<Verify />} />
              <Route path="/auth/pay" element={<Pay />} />
              <Route path="/auth/pay/success" element={<PaySuccess />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </main>
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)} />}
    </div>
  )
}
